import { format, isSameDay, isWithinInterval } from 'date-fns'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { DateCalendar, PickersDay, PickersDayProps } from '@mui/x-date-pickers'

import { Icon } from '@iconify/react'

interface RangeDayProps extends PickersDayProps<Date> {
  rangeStart: Date | null
  rangeEnd: Date | null
}

function RangeDay(props: RangeDayProps) {
  const { day, rangeStart, rangeEnd, ...other } = props
  const theme = useTheme()

  const isStart = !!rangeStart && isSameDay(day, rangeStart)
  const isEnd = !!rangeEnd && isSameDay(day, rangeEnd)
  const isEndpoint = isStart || isEnd
  const inRange =
    !!rangeStart && !!rangeEnd && isWithinInterval(day, { start: rangeStart, end: rangeEnd })

  return (
    <PickersDay
      {...other}
      day={day}
      selected={isEndpoint}
      sx={
        inRange && !isEndpoint
          ? { bgcolor: alpha(theme.palette.primary.main, 0.16) }
          : undefined
      }
    />
  )
}

export type RangeUnit = 'minute' | 'hour' | 'day' | 'week'

export interface RangeChip {
  label: string
  value: string
}

export const CHIP_GROUPS: Record<RangeUnit, RangeChip[]> = {
  minute: [
    { label: 'time.range.1h', value: '1h' },
    { label: 'time.range.6h', value: '6h' },
    { label: 'time.range.12h', value: '12h' },
    { label: 'time.range.1d', value: '1d' },
  ],
  hour: [
    { label: 'time.range.1d', value: '1d' },
    { label: 'time.range.3d', value: '3d' },
    { label: 'time.range.7d', value: '7d' },
    { label: 'time.range.14d', value: '14d' },
  ],
  day: [
    { label: 'time.range.7d', value: '7d' },
    { label: 'time.range.14d', value: '14d' },
    { label: 'time.range.4wk', value: '4wk' },
    { label: 'time.range.8wk', value: '8wk' },
  ],
  week: [
    { label: 'time.range.4wk', value: '4wk' },
    { label: 'time.range.8wk', value: '8wk' },
    { label: 'time.range.12wk', value: '12wk' },
    { label: 'time.range.all', value: 'all' },
  ],
}

export const INTERVAL_BY_UNIT: Record<RangeUnit, string> = {
  minute: '3m',
  hour: '1h',
  day: '1d',
  week: '1wk',
}

export const relativeRangeToMs = (range: string): number => {
  const m = 60 * 1000
  const h = 60 * m
  const d = 24 * h
  const wk = 7 * d
  switch (range) {
    case '15m':
      return 15 * m
    case '30m':
      return 30 * m
    case '1h':
      return h
    case '2h':
      return 2 * h
    case '6h':
      return 6 * h
    case '12h':
      return 12 * h
    case '1d':
      return d
    case '2d':
      return 2 * d
    case '3d':
      return 3 * d
    case '7d':
      return 7 * d
    case '14d':
      return 14 * d
    case '4wk':
      return 4 * wk
    case '8wk':
      return 8 * wk
    case '12wk':
      return 12 * wk
    case 'all':
      return 2 * 365 * d
    default:
      return 15 * m
  }
}

const UNIT_OPTIONS: { label: string; value: RangeUnit }[] = [
  { label: 'time.unit.minute', value: 'minute' },
  { label: 'time.unit.hour', value: 'hour' },
  { label: 'time.unit.day', value: 'day' },
  { label: 'time.unit.week', value: 'week' },
]

export type TimeRangeMode = 'relative' | 'absolute'

export interface TimeRangeSelectorValue {
  unit: RangeUnit
  relativeRange: string
  mode: TimeRangeMode
  startDate: string | null // yyyy-MM-dd, set when mode === 'absolute'
  startTime: string | null // HH:mm, set when mode === 'absolute'
  endDate: string | null // yyyy-MM-dd, set when mode === 'absolute'
  endTime: string | null // HH:mm, set when mode === 'absolute'
}

interface TimeRangeSelectorProps {
  value: TimeRangeSelectorValue
  onChange: (next: TimeRangeSelectorValue) => void
  hasCycle?: boolean
}

export default function TimeRangeSelector({
  value,
  onChange,
  hasCycle = false,
}: TimeRangeSelectorProps) {
  const { t } = useTranslation('iot')
  const theme = useTheme()
  const chips = CHIP_GROUPS[value.unit]

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null)
  const [tempStartTime, setTempStartTime] = useState<string>('00:00')
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null)
  const [tempEndTime, setTempEndTime] = useState<string>('00:00')

  const popoverOpen = Boolean(anchorEl)

  const handleOpenPicker = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
    setTempStartDate(value.startDate ? new Date(`${value.startDate}T00:00:00`) : null)
    setTempStartTime(value.startTime ?? '00:00')
    setTempEndDate(value.endDate ? new Date(`${value.endDate}T00:00:00`) : null)
    setTempEndTime(value.endTime ?? format(new Date(), 'HH:mm'))
  }

  const handleDaySelect = (date: Date | null) => {
    if (!date) return
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Start a fresh selection.
      setTempStartDate(date)
      setTempEndDate(null)
    } else if (date.getTime() < tempStartDate.getTime()) {
      // Tapped a date earlier than the current start -> treat it as the new start.
      setTempStartDate(date)
      setTempEndDate(null)
    } else {
      setTempEndDate(date)
    }
  }

  const isRangeInvalid =
    !!tempStartDate &&
    !!tempEndDate &&
    new Date(`${format(tempStartDate, 'yyyy-MM-dd')}T${tempStartTime}:00`).getTime() >
      new Date(`${format(tempEndDate, 'yyyy-MM-dd')}T${tempEndTime}:00`).getTime()

  const handleApply = () => {
    if (!tempStartDate || !tempEndDate || isRangeInvalid) return
    onChange({
      ...value,
      mode: 'absolute',
      startDate: format(tempStartDate, 'yyyy-MM-dd'),
      startTime: tempStartTime,
      endDate: format(tempEndDate, 'yyyy-MM-dd'),
      endTime: tempEndTime,
    })
    setAnchorEl(null)
  }

  const handleClear = () => {
    onChange({
      ...value,
      mode: 'relative',
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
    })
    setAnchorEl(null)
  }

  const handleUnit = (unit: RangeUnit) => {
    onChange({ ...value, unit, relativeRange: CHIP_GROUPS[unit][0].value })
  }

  const handleChip = (chip: string) => {
    onChange({
      ...value,
      mode: 'relative',
      relativeRange: chip,
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
    })
  }

  const triggerLabel =
    value.mode === 'absolute' &&
    value.startDate &&
    value.startTime &&
    value.endDate &&
    value.endTime
      ? `${format(new Date(`${value.startDate}T00:00:00`), 'dd/MM/yy')} ${
          value.startTime
        } - ${format(new Date(`${value.endDate}T00:00:00`), 'dd/MM/yy')} ${value.endTime}`
      : null

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{t('time.range')}</Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        {triggerLabel ? (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={handleOpenPicker}
            startIcon={<Icon icon="majesticons:calendar" width={16} height={16} />}
            sx={{ borderRadius: 1, borderColor: 'divider', color: 'text.primary', height: 40 }}
          >
            {triggerLabel}
          </Button>
        ) : (
          <IconButton
            onClick={handleOpenPicker}
            sx={{
              width: 40,
              height: 40,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              color: 'text.primary',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Icon icon="majesticons:calendar" width={24} height={24} />
          </IconButton>
        )}

        <TextField
          select
          size="small"
          value={value.unit}
          onChange={(e) => handleUnit(e.target.value as RangeUnit)}
          sx={{ minWidth: 100 }}
        >
          {UNIT_OPTIONS.map((u) => (
            <MenuItem key={u.value} value={u.value}>
              {t(u.label)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
          {t('time.lookback')}
        </Typography>
        {chips.map((c) => {
          const active = value.mode === 'relative' && c.value === value.relativeRange
          const disabled = c.value === 'all' && !hasCycle
          return (
            <Chip
              key={c.value}
              label={t(c.label)}
              variant="outlined"
              disabled={disabled}
              onClick={() => !disabled && handleChip(c.value)}
              sx={{
                borderRadius: 1,
                bgcolor: active
                  ? alpha(theme.palette.primary.main, 0.08)
                  : theme.palette.background.paper,
                borderColor: active ? theme.palette.primary.main : alpha(theme.palette.divider, 1),
                color: active ? theme.palette.primary.main : theme.palette.text.primary,
                fontWeight: active ? 600 : 500,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
          )
        })}
      </Stack>

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }}>
          <DateCalendar
            key={popoverOpen ? 'open' : 'closed'}
            value={null}
            referenceDate={tempStartDate ?? undefined}
            onChange={handleDaySelect}
            maxDate={new Date()}
            slots={{ day: RangeDay as unknown as typeof PickersDay }}
            slotProps={{
              day: {
                rangeStart: tempStartDate,
                rangeEnd: tempEndDate,
              } as never,
            }}
          />

          <Stack direction="row" spacing={2} sx={{ mt: 1, px: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('time.start')}
              </Typography>
              <TextField
                type="time"
                size="small"
                value={tempStartTime}
                onChange={(e) => setTempStartTime(e.target.value)}
                fullWidth
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('time.end')}
              </Typography>
              <TextField
                type="time"
                size="small"
                value={tempEndTime}
                onChange={(e) => setTempEndTime(e.target.value)}
                fullWidth
              />
            </Box>
          </Stack>

          {isRangeInvalid && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, px: 1 }}>
              {t('time.invalid_range')}
            </Typography>
          )}

          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" color="inherit" size="small" onClick={handleClear}>
              {t('action.clear')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              disabled={!tempStartDate || !tempEndDate || isRangeInvalid}
            >
              {t('action.confirm')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}
